import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'etch.settings';

export const DEFAULT_SETTINGS = {
  provider: 'gemini',
  // No hardcoded default (nyxCore action point a10ba442 follow-up): the
  // real per-provider default (PROVIDERS[id].defaultModel, factory.js)
  // already drifted from a literal kept here before, and since `model` is
  // only sent to the server once `providerTouched` is true (same as
  // `provider`, see createSession below), a stale literal here only misled
  // the Settings UI's own pre-selection, never an actual session.
  model: null,
  // false until the user actually picks a provider via Settings (setProvider
  // below). POST /api/sessions falls back to the tenant LLM policy's
  // `defaultProvider` only when the request omits `provider` entirely
  // (routes/api.js: `provider || policy.defaultProvider || 'gemini'`) — so
  // SessionContext.createSession sends `provider`/`model` only once this is
  // true, letting a tenant's policy default (and allowedProviders/euOnly)
  // actually apply to a member who never opened Settings (nyxCore action
  // point a10ba442). An old stored `etch.settings` predating this field
  // reads back as `false` here too, since the spread below only overwrites
  // keys the stored JSON actually has.
  providerTouched: false,
  // No default here (SSRF hardening, nyxCore action point f208bd62): this
  // value rides straight into POST /api/sessions' body, and an explicit
  // request-body URL wins over the tenant's own resolved `ollama_url`
  // setting server-side. Defaulting it to localhost would silently shadow
  // that setting on a hosted instance for every session, not just Ollama
  // ones. `null` until the user sets one via Settings → Ollama; the local
  // admin UI (useOllamaModels.js) falls back to localhost on its own.
  ollamaBaseUrl: null
};

// The literal DEFAULT_SETTINGS.ollamaBaseUrl used to be before the SSRF
// hardening above (nyxCore action point f208bd62). A browser that loaded an
// older build persisted it into `etch.settings` on every mount, regardless of
// provider — so migrate it back to the new default on load.
const LEGACY_OLLAMA_DEFAULT = 'http://localhost:11434';

/**
 * Merges a raw JSON.parse()'d `etch.settings` record onto DEFAULT_SETTINGS,
 * migrating stored values that predate a default change. Exported (rather
 * than kept as a `useState` initializer closure) so it is unit-testable
 * without React/DOM — see tests/unit/web/ollama-default-url.test.js.
 */
export function mergeStoredSettings(stored) {
  const merged = { ...DEFAULT_SETTINGS, ...stored };
  if (merged.ollamaBaseUrl === LEGACY_OLLAMA_DEFAULT) merged.ollamaBaseUrl = null;
  return merged;
}

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettingsState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return mergeStoredSettings(JSON.parse(stored));
      }
    } catch (e) {
      console.warn('Failed to load settings from localStorage:', e);
    }
    return DEFAULT_SETTINGS;
  });

  // Persist settings to localStorage when they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (e) {
      console.warn('Failed to save settings to localStorage:', e);
    }
  }, [settings]);

  const updateSettings = useCallback((updates) => {
    setSettingsState(prev => ({ ...prev, ...updates }));
  }, []);

  const setProvider = useCallback((provider, model = null) => {
    setSettingsState(prev => ({
      ...prev,
      provider,
      model: model || prev.model,
      providerTouched: true
    }));
  }, []);

  const setModel = useCallback((model) => {
    setSettingsState(prev => ({ ...prev, model, providerTouched: true }));
  }, []);

  const setOllamaBaseUrl = useCallback((ollamaBaseUrl) => {
    setSettingsState(prev => ({ ...prev, ollamaBaseUrl }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettingsState(DEFAULT_SETTINGS);
  }, []);

  const value = {
    ...settings,
    updateSettings,
    setProvider,
    setModel,
    setOllamaBaseUrl,
    resetSettings
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

export default SettingsContext;
